import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import { useDocument } from '@nandorojo/swr-firestore';
import {
  CollectionPath,
  SettingCollectionPath,
} from '@typings/collections.enum';
import { User } from '@typings/setting/userType.interface';
import {
  IExplorationPointSetting,
  initialExplorationPointSetting,
} from '@typings/setting/explorationSetting';
import { fuego } from '@store/firebase';

/**
 * 탐색 포인트 시스템
 *
 * 설계 원칙:
 * - Firestore를 단일 소스로 사용 (Recoil 중간 레이어 없음)
 * - 표시값은 저장된 포인트 + 경과 인터벌 수 × 회복량으로 즉시 계산 (별도 저장 없음)
 * - Firestore 쓰기는 (a) 차감 시, (b) 인터벌 완료 시에만 발생
 * - userData 구독은 SWR 캐시 없이 네이티브 onSnapshot 사용 → 모든 브라우저/탭 즉시 동기화
 */
type UseExplorationPointOptions = {
  enabled?: boolean;
  enableTimer?: boolean;
};

export const useExplorationPoint = ({
  enabled = true,
  enableTimer = true,
}: UseExplorationPointOptions = {}) => {
  const currentUserID = useRecoilValue(currentUserIDState);
  const [timeUntilNext, setTimeUntilNext] = useState<number>(0);
  const [userData, setLocalUserData] = useState<User | null | undefined>(
    undefined,
  );
  const shouldFetch = enabled;

  // Firestore 중복 쓰기 방지 (isPendingRef 패턴)
  // - isPendingRef: 타이머 tick 쓰기용 (기존)
  // - isDeductingRef: deductPoints 재진입 차단용 (FIX-B)
  const isPendingRef = useRef(false);
  const isDeductingRef = useRef(false);

  const { data: explorationSettings } = useDocument<IExplorationPointSetting>(
    shouldFetch ? `${SettingCollectionPath.EXPLORATION}` : null,
    { listen: true },
  );

  /**
   * userData: SWR 래퍼 대신 네이티브 onSnapshot 사용
   * - SWR optimistic cache는 탭/브라우저별로 독립적이어서 타 브라우저에 즉시 반영 안 됨
   * - onSnapshot은 Firestore가 직접 모든 구독자에게 푸시하므로 크로스-브라우저 즉시 동기화
   */
  useEffect(() => {
    if (!shouldFetch || !currentUserID) {
      setLocalUserData(undefined);
      return;
    }

    const unsubscribe = fuego.db
      .doc(`${CollectionPath.USERS}/${currentUserID}`)
      .onSnapshot(
        (doc) => {
          setLocalUserData(doc.exists ? (doc.data() as User) : null);
        },
        () => {
          setLocalUserData(null);
        },
      );

    return unsubscribe;
  }, [shouldFetch, currentUserID]);

  /**
   * Firestore 유저 문서 쓰기 (merge)
   * - 반환값이 항상 Promise<void>이므로 null 처리 불필요
   */
  const writeUserData = useCallback(
    (data: Partial<User>): Promise<void> => {
      if (!shouldFetch || !currentUserID) return Promise.resolve();
      return fuego.db
        .doc(`${CollectionPath.USERS}/${currentUserID}`)
        .set(data, { merge: true });
    },
    [shouldFetch, currentUserID],
  );

  const settings = explorationSettings || initialExplorationPointSetting;
  const INTERVAL_MS = settings.acquisitionInterval * 60 * 1000;
  const REWARD = settings.pointsPerInterval;
  const MAX = settings.maxPoints;

  /**
   * 표시 포인트: Firestore 저장값 + 경과 인터벌 수 × reward
   * Firestore에 쓰지 않고 렌더 시 즉시 계산
   *
   * [FIX-2] stored >= MAX와 !lastTime 분기 분리
   * - stored >= MAX → MAX 반환 (earned 계산 불필요)
   * - !lastTime → stored 반환 (타이머에서 lastClaimTime 초기화 유도)
   */
  const explorationPoint = useMemo(() => {
    if (!shouldFetch || !settings.isActive || !userData) return 0;
    const stored = userData.explorationPoint ?? 0;
    const lastTime = userData.lastExplorationPointClaimTime ?? null;
    if (stored >= MAX) return MAX;
    if (!lastTime) return stored;
    const elapsed = Date.now() - lastTime;
    const earned = Math.min(
      Math.floor(elapsed / INTERVAL_MS) * REWARD,
      MAX - stored,
    );
    return stored + earned;
  }, [userData, settings.isActive, INTERVAL_MS, REWARD, MAX]);

  /**
   * 카운트다운 타이머 (UI 전용)
   * 인터벌 완료 시에만 Firestore에 1회 쓰기
   */
  useEffect(() => {
    if (!shouldFetch || !enableTimer || !settings.isActive || !userData) {
      setTimeUntilNext(0);
      return;
    }

    const tick = () => {
      const stored = userData.explorationPoint ?? 0;
      const lastTime = userData.lastExplorationPointClaimTime ?? null;

      // [FIX-2] MAX 체크와 lastTime null 체크를 분리
      // - MAX 이상이면 타이머 불필요
      // - lastTime null이면 최초 1회 초기화 후 다음 tick에서 정상 루프 진입
      if (explorationPoint >= MAX) {
        setTimeUntilNext(0);
        return;
      }
      if (!lastTime) {
        if (!isPendingRef.current) {
          isPendingRef.current = true;
          writeUserData({ lastExplorationPointClaimTime: Date.now() }).finally(
            () => {
              isPendingRef.current = false;
            },
          );
        }
        return;
      }

      const elapsed = Date.now() - lastTime;
      const intervalsElapsed = Math.floor(elapsed / INTERVAL_MS);
      const nextBase = lastTime + (intervalsElapsed + 1) * INTERVAL_MS;
      const remaining = nextBase - Date.now();
      setTimeUntilNext(Math.max(0, remaining));

      // 인터벌 완료 + MAX 미만인 경우에만 Firestore 업데이트
      // [FIX-3] remaining <= 0은 수학적으로 절대 성립하지 않음
      // (nextBase는 항상 미래 시점) → intervalsElapsed > 0으로 교체
      // isPendingRef: userData 갱신 전 중복 쓰기 방지
      if (!(intervalsElapsed > 0 && stored < MAX && !isPendingRef.current))
        return;

      isPendingRef.current = true;
      const newPoints = Math.min(stored + intervalsElapsed * REWARD, MAX);
      const newLastClaimTime = lastTime + intervalsElapsed * INTERVAL_MS;
      writeUserData({
        explorationPoint: newPoints,
        lastExplorationPointClaimTime: newLastClaimTime,
      }).finally(() => {
        isPendingRef.current = false;
      });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [
    shouldFetch,
    enableTimer,
    userData,
    settings.isActive,
    explorationPoint,
    MAX,
    INTERVAL_MS,
    REWARD,
    writeUserData,
  ]);

  /**
   * 포인트 차감 (조사하기, 탐색 보드, 파견, 관리자 테스트 버튼에서 사용)
   *
   * [FIX-A] runTransaction 기반 원자 차감
   * - Firestore 서버 최신값을 트랜잭션 내부에서 재판정 → stale closure 제거
   * - 병렬 차감 요청은 Firestore 가 자동 직렬화/재시도
   *
   * [FIX-B] isDeductingRef 로 로컬 재진입 차단
   * - 연속 클릭 시 트랜잭션 재시도 폭주 방지 + UI 레벨 즉시 거절
   */
  const deductPoints = useCallback(
    async (amount: number): Promise<boolean> => {
      if (!shouldFetch || !currentUserID) return false;
      if (isDeductingRef.current) return false;

      isDeductingRef.current = true;
      try {
        const userRef = fuego.db.doc(
          `${CollectionPath.USERS}/${currentUserID}`,
        );
        const ok = await fuego.db.runTransaction(async (tx) => {
          const snap = await tx.get(userRef);
          if (!snap.exists) return false;
          const u = (snap.data() as User) || {};
          const stored = u.explorationPoint ?? 0;
          const lastTime = u.lastExplorationPointClaimTime ?? null;
          const elapsed = lastTime ? Date.now() - lastTime : 0;
          const intervalsElapsed = lastTime
            ? Math.floor(elapsed / INTERVAL_MS)
            : 0;
          const earned = lastTime
            ? Math.min(intervalsElapsed * REWARD, Math.max(0, MAX - stored))
            : 0;
          const current = Math.min(stored + earned, MAX);

          if (current < amount) return false;

          const newLastClaimTime = lastTime
            ? lastTime + intervalsElapsed * INTERVAL_MS
            : Date.now();
          tx.set(
            userRef,
            {
              explorationPoint: Math.max(0, current - amount),
              lastExplorationPointClaimTime: newLastClaimTime,
            },
            { merge: true },
          );
          return true;
        });
        return ok;
      } catch (e) {
        console.error('[useExplorationPoint] deductPoints 실패', e);
        return false;
      } finally {
        isDeductingRef.current = false;
      }
    },
    [shouldFetch, currentUserID, INTERVAL_MS, REWARD, MAX],
  );

  /**
   * 시간 포맷팅 (시:분:초)
   */
  const formatTimeUntilNext = useCallback(() => {
    if (!settings.isActive) return null;
    if (explorationPoint >= MAX) return null;
    if (timeUntilNext <= 0) return null;

    const hours = Math.floor(timeUntilNext / (1000 * 60 * 60));
    const minutes = Math.floor(
      (timeUntilNext % (1000 * 60 * 60)) / (1000 * 60),
    );
    const seconds = Math.floor((timeUntilNext % (1000 * 60)) / 1000);

    if (hours > 0) return `${hours}시간 ${minutes}분 ${seconds}초`;
    if (minutes > 0) return `${minutes}분 ${seconds}초`;
    return `${seconds}초`;
  }, [timeUntilNext, settings.isActive, explorationPoint, MAX]);

  return {
    explorationPoint,
    timeUntilNext,
    formatTimeUntilNext,
    isActive: settings.isActive,
    maxPoints: MAX,
    deductPoints,
    // 하위 호환성 유지 (기존 useExplorationPoints 사용처에서 점진적 마이그레이션)
    useExplorationPoints: deductPoints,
  };
};
